import { Box, Grid } from "@mui/material";
import { ContentBoundary } from "../ContentBoundary";
import { BorderColorButton } from "../BorderColorButton";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

const ServiceDescriptionSection: React.FC<ServiceDescriptionSectionProps> = ({
  backgroundColor,
  backgroundImageUrl,
  direction = "left",
  textColor,
  titleColor,
  title,
  description,
}) => {
  const navigate = useNavigate();
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  const handleMoreInfoClick = () => {
    const serviceSlug = title
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/&/g, "and");
    navigate(`/service/${serviceSlug}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const rect = parallaxRef.current.getBoundingClientRect();
        const elementTop = rect.top;
        const elementHeight = rect.height;
        const windowHeight = window.innerHeight;

        // Calculate how much of the element is visible
        const elementCenter = elementTop + elementHeight / 2;
        const windowCenter = windowHeight / 2;

        // Calculate parallax offset based on element position relative to viewport
        const parallaxOffset = (elementCenter - windowCenter) * 0.08;
        setOffset(parallaxOffset);
      }
    };

    // Initial calculation
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          top: "0px",
          left: "0px",
          width: "100%",
          display: "flex",
          bottom: "0px",
        }}
      >
        <Box
          sx={{
            backgroundColor,
            order: direction === "left" ? 0 : 1,
            flexBasis: { xs: "100%", md: "50%" },
          }}
        />
        <Box
          ref={parallaxRef}
          sx={{
            flexBasis: "50%",
            display: { xs: "none", md: "block" },
            minHeight: "400px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-15%",
              left: 0,
              width: "100%",
              height: "130%",
              backgroundImage: `url(${backgroundImageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              transform: `translateY(${offset}px)`,
              willChange: "transform",
              transition: "transform 0.3s ease-out",
            }}
          />
        </Box>
      </Box>
      <Box sx={{ zIndex: 1, position: "relative" }}>
        <ContentBoundary>
          <Box sx={{ display: "flex", gap: { xs: 0, md: 6 } }}>
            <Box
              sx={{
                flexBasis: { xs: "100%", md: "50%" },
                order: direction === "left" ? 0 : 1,
                py: { xs: 4, md: 6 },
                px: { xs: 2, md: 0 },
              }}
            >
              <Grid container direction="column" gap={3}>
                <Grid item>
                  <Box
                    sx={{
                      typography: { xs: "h4", md: "h3" },
                    }}
                  >
                    <Box sx={{ fontWeight: "bold" }} color={titleColor}>
                      {title}
                    </Box>
                  </Box>
                </Grid>
                <Grid item>
                  <Box
                    color={textColor}
                    sx={{
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      lineHeight: 1.6,
                    }}
                  >
                    {description}
                  </Box>
                </Grid>
                <Grid item>
                  <Box sx={{ mt: 3 }}>
                    <BorderColorButton
                      onClick={handleMoreInfoClick}
                      variant="outlined"
                      size="large"
                      sx={{
                        color: textColor,
                        borderColor: textColor,
                        fontSize: { xs: "0.9rem", md: "1rem" },
                        px: { xs: 3, md: 4 },
                        py: { xs: 1, md: 1.5 },
                        "&:hover": {
                          backgroundColor: textColor,
                          color: backgroundColor,
                        },
                      }}
                    >
                      More Info
                    </BorderColorButton>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{ flexBasis: "50%", display: { xs: "none", md: "block" } }}
            ></Box>
          </Box>
        </ContentBoundary>
      </Box>
    </Box>
  );
};

export interface ServiceDescriptionSectionProps {
  backgroundColor: string;
  backgroundImageUrl: string;
  direction: "left" | "right";
  textColor: string;
  title: string;
  titleColor: string;
  description: string | React.ReactElement;
}

export default ServiceDescriptionSection;
